import React, { useEffect, useState, useContext } from "react";
import toast from "react-hot-toast";
import api from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";

export default function SeekerDashboardPage() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState({ deals: [], confirmedDeals: 0 });
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await api.get("/dashboard/seeker");
        setData(res.data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handlePay(dealId) {
    if (!window.confirm("Proceed to pay service charge for this deal?")) return;

    try {
      setPayingId(dealId);
      const res = await api.post(`/deals/${dealId}/pay`);

      // update that one deal in local state
      setData((prev) => ({
        ...prev,
        deals: prev.deals.map((d) => (d.id === dealId ? res.data : d)),
      }));

      toast.success("Payment successful. Waiting for admin verification.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Payment failed");
    } finally {
      setPayingId(null);
    }
  }

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-10">Loading...</div>;
  }

  // 🔴 Split deals: pending vs completed
  const pendingDeals = data.deals.filter(
    (d) => d.status !== "CONFIRMED"
  );
  const completedDeals = data.deals.filter(
    (d) => d.status === "CONFIRMED"
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-semibold">My Dashboard</h1>
      <p className="text-gray-500">Track your room search and bookings</p>

      <div className="grid grid-cols-4 gap-6 mt-6">
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <div className="text-3xl font-bold">{data.deals.length}</div>
          <div className="text-gray-500">Total Deals</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <div className="text-3xl font-bold">
            {completedDeals.length}
          </div>
          <div className="text-gray-500">Completed Deals</div>
        </div>
      </div>

      {/* 🔴 ACTIVE / PENDING DEALS (deal requests) */}
      <div className="mt-8 bg-white rounded-xl shadow p-6">
        <h2 className="font-semibold mb-4">My Active Deal Requests</h2>

        {pendingDeals.length === 0 ? (
          <p className="text-sm text-gray-500">
            You have no active deal requests right now.
          </p>
        ) : (
          pendingDeals.map((d) => {
            const canPay =
              d.seekerConfirmed &&
              d.ownerConfirmed &&
              !d.paid &&
              (d.status === "OWNER_CONFIRMED" ||
                d.status === "PAYMENT_IN_PROGRESS" ||
                d.status === "AWAITING_ADMIN_VERIFY");

            return (
              <div
                key={d.id}
                className="border-b last:border-0 py-3 flex items-center justify-between"
              >
                <div>
                  <div className="font-semibold">{d.listing.title}</div>
                  <div className="text-gray-500 text-sm">
                    Rs. {d.rentAmount} • Status: {d.status}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Seeker confirmed: {d.seekerConfirmed ? "Yes" : "No"} | Owner
                    confirmed: {d.ownerConfirmed ? "Yes" : "No"} | Paid:{" "}
                    {d.paid ? "Yes" : "No"}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-primary font-semibold">
                    Rs. {d.serviceCharge} fee
                  </div>

                  {canPay && (
                    <button
                      onClick={() => handlePay(d.id)}
                      disabled={payingId === d.id}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {payingId === d.id ? "Processing..." : "Pay Now"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 🔴 COMPLETED DEALS SECTION */}
      <div className="mt-8 bg-white rounded-xl shadow p-6">
        <h2 className="font-semibold mb-4">Completed Deals</h2>

        {completedDeals.length === 0 ? (
          <p className="text-sm text-gray-500">
            You have no completed deals yet.
          </p>
        ) : (
          completedDeals.map((d) => (
            <div
              key={d.id}
              className="border-b last:border-0 py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-semibold">{d.listing.title}</div>
                <div className="text-gray-500 text-sm">
                  Rs. {d.rentAmount} • Status: {d.status}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Paid: {d.paid ? "Yes" : "No"} | Admin verified:{" "}
                  {d.adminVerified ? "Yes" : "No"}
                </div>
              </div>
              <div className="text-primary font-semibold">
                Rs. {d.serviceCharge} fee
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
